/**
 * Client-side handling for the server's Layer-2 entitlement gate (MUS-532).
 *
 * When entitlement enforcement is enabled server-side, a signed-in but
 * non-paying user who hits a gated content/gameplay endpoint gets a 403. The
 * client already blocks those pages with the paywall modal, so in the common
 * case it never calls them — but a load race (a page mounts and fires a content
 * request in the moment before the paywall renders) can still produce one. This
 * module lets the shared axios error layer turn that 403 into "show the paywall"
 * instead of a generic error / blank / crash.
 *
 * Two responsibilities, deliberately kept together and dependency-free (no
 * React, no redux) so the axios interceptor can import them and they can be
 * unit-tested in isolation:
 *
 *   1. isEntitlementDenied() — recognise the gate's denial, precisely.
 *   2. a tiny pub/sub — the interceptor is not a React component, so it cannot
 *      open the paywall directly. It calls notifyEntitlementRequired(); the
 *      React layer (PaymentFlow) subscribes via onEntitlementRequired(). This
 *      mirrors AuthService's onSessionInvalid listener pattern.
 */
import { AxiosError } from 'axios';

/**
 * The exact message the backend Layer-2 guard throws on an enforce-mode denial:
 * `new ForbiddenException('Active subscription required')`, which serialises to
 * HTTP 403 `{ statusCode: 403, message: 'Active subscription required',
 * error: 'Forbidden' }`.
 *
 * We key on this marker rather than a client-side path denylist because it is
 * the *precise* "not entitled" signal, and matching it cannot over-catch:
 *   - OPEN_RESOURCES (account / billing / purchase / webhooks / admin) are never
 *     gated, so they never emit it;
 *   - an ordinary authorization 403 carries a different message;
 *   - a 401 (auth, not entitlement) is a different status entirely.
 *
 * Contract source of truth: backend `src/entitlement/entitlement.guard.ts`. If
 * the server ever changes the message, this constant must change with it. The
 * failure direction is safe: a mismatch regresses to surfacing the error (the
 * pre-MUS-532 behaviour), never to wrongly swallowing an unrelated 403 into the
 * paywall.
 */
export const ENTITLEMENT_DENIED_MESSAGE = 'Active subscription required';

/**
 * True iff `error` is the entitlement gate's 403 denial. Tolerant of NestJS
 * serialising `message` as either a string or a string[] (a ForbiddenException
 * with a single string message yields a string, but we don't want to be brittle
 * to that shape).
 */
export function isEntitlementDenied(error: unknown): boolean {
  if (!(error instanceof AxiosError)) return false;
  if (error.response?.status !== 403) return false;

  const message = (error.response?.data as { message?: unknown } | undefined)?.message;
  if (typeof message === 'string') {
    return message === ENTITLEMENT_DENIED_MESSAGE;
  }
  if (Array.isArray(message)) {
    return message.includes(ENTITLEMENT_DENIED_MESSAGE);
  }
  return false;
}

type EntitlementRequiredListener = () => void;

const listeners = new Set<EntitlementRequiredListener>();

/**
 * Subscribe to entitlement-required events. Returns an unsubscribe function.
 * The React layer (PaymentFlow) uses this to force the paywall on a server
 * denial.
 */
export function onEntitlementRequired(listener: EntitlementRequiredListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Fire all entitlement-required listeners. Called by the axios error layer when
 * it sees the gate's denial. A throwing listener must not stop the others (or
 * bubble back into the interceptor), so each is isolated.
 */
export function notifyEntitlementRequired(): void {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // A listener error must not break the axios rejection path or sibling
      // listeners; swallow it here.
    }
  });
}
